/**
 * Update Project Use Case
 *
 * Updates an existing project with the provided data.
 * This use case handles the business logic for project updates,
 * including validation and data transformation.
 */

import type { IProjectsGateway } from "../ports/IProjectsGateway";
import type { UpdateProjectDTO, ProjectDTO } from "../dtos/ProjectDTO";

export class UpdateProjectUseCase {
  constructor(private readonly projectsGateway: IProjectsGateway) {}

  async execute(
    projectId: string,
    data: UpdateProjectDTO
  ): Promise<ProjectDTO> {
    // Validate project ID
    if (!projectId || projectId.trim().length === 0) {
      throw new Error("Project ID is required");
    }

    // Trim and normalize input if provided
    const normalizedData: UpdateProjectDTO = {};

    if (data.name !== undefined) {
      const trimmedName = data.name.trim();
      if (trimmedName.length === 0) {
        throw new Error("Project name cannot be empty");
      }
      normalizedData.name = trimmedName;
    }

    if (data.description !== undefined) {
      normalizedData.description = data.description.trim();
    }

    if (data.status !== undefined) {
      // Validate status
      if (!["new", "opened", "closed"].includes(data.status)) {
        throw new Error("Invalid project status");
      }
      normalizedData.status = data.status;
    }

    if (data.researchGoals !== undefined) {
      normalizedData.researchGoals = data.researchGoals.trim();
    }

    if (data.interviewGuide !== undefined) {
      normalizedData.interviewGuide = data.interviewGuide.trim();
    }

    return this.projectsGateway.updateProject(projectId, normalizedData);
  }
}
